import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Edit3,
  ArrowLeft,
  Save,
  Image as ImageIcon,
  Sparkles,
  Layers,
  CheckSquare,
  Plus,
  X,
  Upload,
} from "lucide-react";
import QuestionBankClient from "../data/api/QuestionBankClient";
import { ModuleItem } from "../data/interfaces/QuestionBankInterfaces";
import CustomSelect from "../components/CustomSelect";
import RightAnswerSelect from "../components/RightAnswerSelect";
import Loader from "../../../../app/shared/components/Loader";
import Alert from "../../../../app/shared/components/Alert";
import ImagePreviewModal from "../../../../app/shared/components/ImagePreviewModal";
import "../styles/QuestionBankStyles.css";

const EditQuestionPage: React.FC = () => {
  const location = useLocation();
  const params = useParams<{ questionId: string }>();
  const navigate = useNavigate();
  const questionId = location.state?.questionId || params.questionId || "";

  const [difficultyLevel, setDifficultyLevel] = useState<string>("Easy");
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [moduleId, setModuleId] = useState<string>("");
  const [moduleName, setModuleName] = useState<string>("");

  // Add Module Modal state
  const [showAddModuleModal, setShowAddModuleModal] = useState<boolean>(false);
  const [newModuleName, setNewModuleName] = useState<string>("");
  const [savingModule, setSavingModule] = useState<boolean>(false);

  const [questionText, setQuestionText] = useState<string>("");
  const [explanation, setExplanation] = useState<string>("");
  const [concepts, setConcepts] = useState<string>("");

  // MCQ Options
  const [optA, setOptA] = useState<string>("");
  const [optB, setOptB] = useState<string>("");
  const [optC, setOptC] = useState<string>("");
  const [optD, setOptD] = useState<string>("");
  const [correctOption, setCorrectOption] = useState<string>("A");

  // Optional Image Upload File & Existing URL
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [previewModalSrc, setPreviewModalSrc] = useState<string | null>(null);

  const [fetching, setFetching] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "success" | "warning" | "error";
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
  });

  const fetchModulesAndQuestion = async () => {
    if (!questionId) return;
    setFetching(true);
    try {
      // 1. Fetch modules
      const modRes = await QuestionBankClient.listModules();
      let modItems: ModuleItem[] = [];
      if (modRes && modRes.modules) {
        modItems = modRes.modules.map((m: any) => ({
          id: m.module_id,
          name: m.module_name,
          industry_id: "ind_standard",
        }));
        setModules(modItems);
      }

      // 2. Fetch question details
      const qRes = await QuestionBankClient.getQuestion(questionId);
      const qData = qRes.question || qRes;

      if (qData) {
        setQuestionText(qData.question || "");
        setDifficultyLevel(qData.difficulty_level || "Easy");
        setExplanation(qData.explanation || "");
        setConcepts(
          Array.isArray(qData.concepts)
            ? qData.concepts.join(", ")
            : qData.concepts || "",
        );
        setExistingImageUrl(qData.image_url || null);

        if (qData.module_id) {
          setModuleId(qData.module_id);
          const found = modItems.find((m) => m.id === qData.module_id);
          setModuleName(found ? found.name : qData.module_name || "");
        } else if (modItems.length > 0) {
          setModuleId(modItems[0].id);
          setModuleName(modItems[0].name);
        }

        if (qData.answers && qData.answers.length > 0) {
          setOptA(qData.answers[0]?.answer || "");
          if (qData.answers[0]?.correct_answer) setCorrectOption("A");

          if (qData.answers[1]) {
            setOptB(qData.answers[1].answer || "");
            if (qData.answers[1].correct_answer) setCorrectOption("B");
          }
          if (qData.answers[2]) {
            setOptC(qData.answers[2].answer || "");
            if (qData.answers[2].correct_answer) setCorrectOption("C");
          }
          if (qData.answers[3]) {
            setOptD(qData.answers[3].answer || "");
            if (qData.answers[3].correct_answer) setCorrectOption("D");
          }
        }
      }
    } catch (err) {
      console.error("Error populating edit form:", err);
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Could not load question data for editing.",
        type: "error",
      });
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchModulesAndQuestion();
  }, [questionId]);

  const handleCreateModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newModuleName.trim()) return;
    setSavingModule(true);
    try {
      const res = await QuestionBankClient.createModule(newModuleName.trim());
      setShowAddModuleModal(false);
      setNewModuleName("");
      const modRes = await QuestionBankClient.listModules();
      if (modRes && modRes.modules) {
        const modItems = modRes.modules.map((m: any) => ({
          id: m.module_id,
          name: m.module_name,
          industry_id: "ind_standard",
        }));
        setModules(modItems);
      }
      if (res && res.module) {
        setModuleId(res.module.module_id);
        setModuleName(res.module.module_name);
      }
      setAlertConfig({
        show: true,
        title: "Module Created",
        message: `Module '${newModuleName}' created and selected successfully.`,
        type: "success",
      });
    } catch (err: any) {
      console.error("Error creating module:", err);
    } finally {
      setSavingModule(false);
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (files.length > 1) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "You can only upload 1 image per question. Please select a single image file.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Invalid file type. Only image files (PNG, JPG, JPEG, WEBP, SVG) are allowed.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Image file size exceeds the allowed limit of 5MB.",
        type: "error",
      });
      e.target.value = "";
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const MAX_WIDTH = 1920;
      const MAX_HEIGHT = 1080;
      if (img.width > MAX_WIDTH || img.height > MAX_HEIGHT) {
        setAlertConfig({
          show: true,
          title: "Error",
          message: `Image dimensions exceed the allowed limit. Maximum allowed is ${MAX_WIDTH}x${MAX_HEIGHT}, but got ${img.width}x${img.height}.`,
          type: "error",
        });
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
        return;
      }
      setImageFile(file);
      setImagePreviewUrl(objectUrl);
    };
    img.onerror = () => {
      setAlertConfig({
        show: true,
        title: "Error",
        message: "Failed to load the selected image file. Please choose a valid image file.",
        type: "error",
      });
      URL.revokeObjectURL(objectUrl);
      e.target.value = "";
    };
    img.src = objectUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionId) return;
    if (!questionText.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "Question description is required.",
        type: "warning",
      });
      return;
    }
    if (!optA.trim() || !optB.trim() || !optC.trim() || !optD.trim()) {
      setAlertConfig({
        show: true,
        title: "Validation Error",
        message: "All 4 Options (Option A, B, C, and D) are mandatory.",
        type: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      const answersPayload = [
        { answer: optA.trim(), correct_answer: correctOption === "A" },
        { answer: optB.trim(), correct_answer: correctOption === "B" },
        { answer: optC.trim(), correct_answer: correctOption === "C" },
        { answer: optD.trim(), correct_answer: correctOption === "D" },
      ];

      let finalImageUrl = existingImageUrl || undefined;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        formData.append("question_id", questionId);
        formData.append("module_id", moduleId);
        const imgRes = await QuestionBankClient.uploadQuestionImage(formData);
        finalImageUrl = imgRes.image_url;
      }

      const updatePayload = {
        question: questionText.trim(),
        difficulty_level: difficultyLevel as any,
        module_id: moduleId,
        module_name: moduleName,
        explanation: explanation || undefined,
        concepts: concepts
          ? concepts
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : undefined,
        answers: answersPayload as any,
        image_url: finalImageUrl,
      };

      await QuestionBankClient.updateQuestion(questionId, updatePayload);
      setAlertConfig({
        show: true,
        title: "Question Updated",
        message: "Changes saved successfully!",
        type: "success",
      });

      setTimeout(() => {
        navigate("/dashboard/question-bank/view", { state: { questionId } });
      }, 1000);
    } catch (err: any) {
      console.error("Error updating question:", err);
      setAlertConfig({
        show: true,
        title: "Update Failed",
        message:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Failed to update question.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (fetching) {
    return <Loader isLoading={true} message="Loading Question for Edit..." />;
  }

  return (
    <div className="qb-container">
      <Loader
        isLoading={saving || savingModule}
        message={
          savingModule ? "Saving Module to DB..." : "Updating Question..."
        }
      />

      <Alert
        show={alertConfig.show}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig((prev) => ({ ...prev, show: false }))}
      />

      {/* Header */}
      <div className="qb-header">
        <div>
          <h1 className="qb-title">
            <Edit3 size={24} /> Edit Question
          </h1>
          <p className="qb-subtitle">
            Update question prompt, options, correct key, and diagram asset.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="qb-single-frame-form">
        <div className="qb-form-body-scroll">
          {/* Section 1: Compact Module & Difficulty Bar */}
          <div className="qb-compact-module-bar">
            <div className="qb-compact-bar-item">
              <span className="qb-compact-bar-label">Difficulty Level:</span>
              <div className="qb-difficulty-pills" style={{ gap: "6px" }}>
                {["Easy", "Medium", "Hard"].map((diff) => (
                  <div
                    key={diff}
                    className={`qb-diff-pill ${difficultyLevel === diff ? `active-${diff.toLowerCase()}` : ""}`}
                    onClick={() => setDifficultyLevel(diff)}
                    style={{ padding: "6px 16px", fontSize: "13px" }}
                  >
                    {diff}
                  </div>
                ))}
              </div>
            </div>

            <div className="qb-compact-bar-item" style={{ gap: "10px" }}>
              <span className="qb-compact-bar-label">
                Target Module <span style={{ color: "#ef4444" }}>*</span>:
              </span>
              {modules.length > 0 ? (
                <>
                  <div style={{ width: "220px" }}>
                    <CustomSelect
                      options={modules.map((m) => ({ value: m.id, label: m.name }))}
                      value={moduleId}
                      onChange={(val) => {
                        setModuleId(val);
                        const selectedMod = modules.find((m) => m.id === val);
                        if (selectedMod) setModuleName(selectedMod.name);
                      }}
                      placeholder="Select Module..."
                    />
                  </div>
                  <button
                    type="button"
                    className="qb-btn qb-btn-outline"
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setShowAddModuleModal(true)}
                  >
                    <Plus size={14} /> Add New Module
                  </button>
                </>
              ) : (
                <div
                  style={{
                    padding: "6px 14px",
                    backgroundColor: "#FEF3C7",
                    borderRadius: "8px",
                    color: "#92400E",
                    fontSize: "13px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px",
                    minWidth: "260px",
                  }}
                >
                  <span>No modules found</span>
                  <button
                    type="button"
                    className="qb-btn qb-btn-primary"
                    style={{ padding: "4px 12px", fontSize: "12px", whiteSpace: "nowrap" }}
                    onClick={() => setShowAddModuleModal(true)}
                  >
                    Create Module
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Side-by-Side Question Description & Image Upload (Image 3 Layout) */}
          <div className="qb-desc-image-grid">
            {/* Left Column: Question Description */}
            <div className="qb-desc-card">
              <label className="qb-desc-label">
                Question Description <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                className="qb-desc-textarea"
                placeholder="Type your question description..."
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                required
              />
            </div>

            {/* Right Column: Upload Image (Image 3 Layout Box) */}
            <div className="qb-image-upload-card">
              <label className="qb-image-upload-label">Upload Image</label>
              <div className="qb-image-dropzone-box">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  style={{ display: "none" }}
                  id="qb-edit-image-upload-input"
                />
                {imagePreviewUrl || existingImageUrl ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        display: "inline-block",
                      }}
                      onClick={() =>
                        setPreviewModalSrc(imagePreviewUrl || existingImageUrl)
                      }
                      title="Click to view image preview"
                    >
                      <img
                        src={imagePreviewUrl || existingImageUrl || ""}
                        alt="Asset Preview"
                        className="qb-clickable-preview-img"
                        style={{
                          height: "50px",
                          maxWidth: "100px",
                          objectFit: "contain",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                          transition: "all 0.2s ease",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        marginLeft: "auto",
                      }}
                    >
                      <label
                        htmlFor="qb-edit-image-upload-input"
                        className="qb-image-upload-btn"
                        style={{ padding: "4px 10px", fontSize: "11px" }}
                      >
                        Replace
                      </label>
                      {imagePreviewUrl && (
                        <button
                          type="button"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreviewUrl(null);
                          }}
                          className="qb-btn qb-btn-danger"
                          style={{ padding: "2px 8px", fontSize: "11px" }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="qb-edit-image-upload-input"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      textAlign: "center",
                      gap: "4px",
                    }}
                  >
                    <ImageIcon size={22} color="#0d6e5b" style={{ opacity: 0.8 }} />
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#475569",
                        fontWeight: "600",
                        textAlign: "center",
                      }}
                    >
                      Click to upload image asset (optional)
                    </span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Section 3 & 4: Response Options, Concepts & Explanation Container */}
          <div className="qb-response-section-compact">
            {/* Header: Title on Left, Right Answer Select on Top Right */}
            <div className="qb-response-header">
              <span className="qb-response-title">Response Options</span>
              <RightAnswerSelect
                value={correctOption}
                onChange={setCorrectOption}
              />
            </div>

            {/* 4-Column Options Grid */}
            <div className="qb-options-grid-4col">
              {[
                { key: "A", val: optA, setVal: setOptA },
                { key: "B", val: optB, setVal: setOptB },
                { key: "C", val: optC, setVal: setOptC },
                { key: "D", val: optD, setVal: setOptD },
              ].map((item) => (
                <div
                  key={item.key}
                  className={`qb-compact-option-card ${correctOption === item.key ? "active-correct" : ""}`}
                  onClick={() => setCorrectOption(item.key)}
                >
                  <div className="qb-compact-option-header">
                    <span className="qb-option-letter-badge">{item.key}</span>
                  </div>
                  <textarea
                    className="qb-option-textarea"
                    placeholder={`Option ${item.key} description...`}
                    value={item.val}
                    onChange={(e) => item.setVal(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    required
                  />
                </div>
              ))}
            </div>

            {/* Bottom 2-Column Layout: Concepts & Explanation */}
            <div className="qb-bottom-2col-grid">
              <div className="qb-bottom-col-card">
                <label className="qb-bottom-col-label">Concepts</label>
                <textarea
                  className="qb-bottom-textarea"
                  placeholder="Concept-1, Concept-2"
                  value={concepts}
                  onChange={(e) => setConcepts(e.target.value)}
                />
              </div>

              <div className="qb-bottom-col-card">
                <label className="qb-bottom-col-label">
                  Explanation (Optional)
                </label>
                <textarea
                  className="qb-bottom-textarea"
                  placeholder="Explanation for the correct answer..."
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pinned Fixed Bottom Action Bar */}
        <div className="qb-fixed-action-bar">
          <button
            type="button"
            className="qb-btn qb-btn-outline"
            onClick={() =>
              navigate("/dashboard/question-bank/view", {
                state: { questionId },
              })
            }
            style={{ padding: "8px 20px" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="qb-btn qb-btn-primary"
            style={{ padding: "8px 24px" }}
          >
            <Save size={16} /> Save Changes
          </button>
        </div>
      </form>

      {/* Add New Module Modal */}
      {showAddModuleModal && ReactDOM.createPortal(
        <div
          className="qb-modal-overlay"
          onClick={() => setShowAddModuleModal(false)}
        >
          <div
            className="qb-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "480px" }}
          >
            <div className="qb-modal-header">
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#07473b",
                }}
              >
                Create New Module
              </h3>
              <button
                className="qb-modal-close"
                onClick={() => setShowAddModuleModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateModule}>
              <div className="qb-form-group">
                <label className="qb-label">
                  Module Name <span style={{ color: "#ef4444" }}>*</span>:
                </label>
                <input
                  type="text"
                  className="qb-input"
                  placeholder="e.g. System Design, Cloud Computing, Algorithms"
                  value={newModuleName}
                  onChange={(e) => setNewModuleName(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="qb-btn qb-btn-outline"
                  onClick={() => setShowAddModuleModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="qb-btn qb-btn-primary">
                  Create Module
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      {/* Image Preview Lightbox Modal */}
      <ImagePreviewModal
        src={previewModalSrc}
        onClose={() => setPreviewModalSrc(null)}
      />
    </div>
  );
};

export default EditQuestionPage;
